try {
  /**
   * @typedef {Object} BundleLinePayload
   * @property {string|number} id
   * @property {number} quantity
   * @property {Record<string, any>} [properties]
   */

  /**
   * @typedef {Object} TeamStoreMetaEntry
   * @property {Record<string, any>} metaobject
   * @property {Record<string, any>|null} [teamStoreCollection]
   */

  class TeamStoreBundleCart {
    static REQUIRED_PROPERTIES = [
      '_ts_bundle_id',
      '_ts_bundle_price',
      '_ts_bundle_discount',
      '_bundle_name',
      '_line_final_price',
    ];

    static TEAM_STORE_METAOBJECT_PROPERTIES = [
      'sleeve_print',
      'back_print',
      'opportunity_id',
      'entry_sku',
      'display_name',
      'upcharge',
      'min_order_qty',
      'version_number',
      'customization_embellishment_type',
      'personalization',
    ];

    constructor() {
      /** @type {any|null} */
      this.CartDrawer = document.querySelector('cart-drawer');

      this.viewModel = this.getViewModel(
        document.querySelector('.product-container')
      );

      /** @type {HTMLElement[]} */
      this.bundleRadiosRoots = Array.from(
        document.querySelectorAll('bundle-variant-radios')
      );

      this._isSubmitting = false;

      this._onAddToCartClick = this._onAddToCartClick.bind(this);
      this._onMetaobjectDataRetrieved = this._syncAddToCartState.bind(this);

      this.init();
    }

    /**
     * @param {HTMLElement|null} scope
     * @returns {{
     *   $root: HTMLElement|null,
     *   $addToCartButton: HTMLButtonElement|null,
     *   $errorWrapper: HTMLElement|null,
     *   $errorMessage: HTMLElement|null
     *   $bundleName: HTMLElement|null
     * }}
     */
    getViewModel(scope) {
      const root = scope || document.body;

      const addToCartEl = root.querySelector('.product-form__submit');
      const wrapperEl = root.querySelector(
        '.product-form__error-message-wrapper'
      );
      const msgEl = root.querySelector('.product-form__error-message');

      return {
        $root: root,
        $addToCartButton:
          addToCartEl instanceof HTMLButtonElement ? addToCartEl : null,
        $errorWrapper: wrapperEl instanceof HTMLElement ? wrapperEl : null,
        $errorMessage: msgEl instanceof HTMLElement ? msgEl : null,
        $bundleName: root.querySelector('.product__title'),
      };
    }

    init() {
      if (!this.bundleRadiosRoots.length) {
        return;
      }
      const { $addToCartButton } = this.viewModel;

      $addToCartButton?.addEventListener('click', this._onAddToCartClick);
      window.addEventListener(
        'metaobjectDataRetrieved',
        this._onMetaobjectDataRetrieved
      );
      this._syncAddToCartState();
      this._checkStoreClosedState();
    }

    _getOpportunityNumber() {
      return String(this.teamProductData?.opportunityNumber || '').trim();
    }

    /**
     * Builds a metaobject handle for a team store.
      *
      * @param {string | null} parentSku - Parent SKU of the team product.
      * @param {string | number} [opportunityNumber=this._getOpportunityNumber()] - Opportunity number used for handle generation.
      * @returns {string | null} Generated handle or null if inputs are missing.
    */
    _buildMetaobjectHandle(parentSku, opportunityNumber = this._getOpportunityNumber()) {
      if (!parentSku || !opportunityNumber) return null;

      return (
        window.teamStoreUtilities?.buildTeamStoreHandle?.({
          teamParentSku: parentSku,
          opportunityNumber,
        }) || null
      );
    }

    _hasResolvedBundleMetaobjects() {
      const opportunityNumber = this._getOpportunityNumber();

      if (!opportunityNumber) return false;

      return this.bundleRadiosRoots.every((root) => {
        const parentSku = root.dataset.parentSku;
        if (!parentSku) return true; // Return true for non team store products

        const key = this._buildMetaobjectHandle(parentSku, opportunityNumber);
        if (!key) return false;

        return Boolean(window.TeamStoreMetaobjects?.[key]?.metaobject);
      });
    }

    _allSelectionsAvailable() {
      return (
        this.bundleRadiosRoots.length > 0 &&
        this.bundleRadiosRoots.every(
          (root) => root.dataset.isAvailable === 'true'
        )
      );
    }

    _syncAddToCartState() {
      const { $addToCartButton } = this.viewModel;
      if (!$addToCartButton) return;

      if (!this._hasResolvedBundleMetaobjects()) {
        $addToCartButton.disabled = true;
        $addToCartButton.setAttribute('aria-disabled', 'true');
        $addToCartButton.setAttribute('data-team-store-context-locked', 'true');
        return;
      }

      $addToCartButton.removeAttribute('data-team-store-context-locked');

      if ($addToCartButton.hasAttribute('store-closed') || this._isSubmitting) {
        return;
      }

      const canSubmit = this._allSelectionsAvailable();
      $addToCartButton.disabled = !canSubmit;
      $addToCartButton.setAttribute('aria-disabled', canSubmit ? 'false' : 'true');
    }

    /**
     * Add to Cart click handler.
     *
     * @param {MouseEvent} event
     * @private
     */
    async _onAddToCartClick(event) {
      event.preventDefault();

      if (this._isSubmitting) return;

      this._clearError();

      const parentVariantId = this.bundleVariantId;
      const bundleVariants = this._collectSelectedBundleVariants();

      if (!this._validateBeforeSubmit({ parentVariantId, bundleVariants })) {
        return;
      }

      /** @type {BundleLinePayload[]} */
      const items = this._buildItemsPayload(bundleVariants);

      if (!this._validateItemsProperties(items)) {
        return;
      }

      await this._submitItems(items);
    }

    /**
     * Validate all preconditions before building payload.
     *
     * @param {{ parentVariantId: string|null|undefined, bundleVariants: { variantId: string|null, metaKey: string|null }[] }} params
     * @returns {boolean}
     * @private
     */
    _validateBeforeSubmit({ parentVariantId, bundleVariants }) {
      if (!this._hasResolvedBundleMetaobjects()) {
        this._showError(
          'Unable to add bundle to cart. Missing opportunity number or team product data.'
        );
        this._syncAddToCartState();
        return false;
      }

      if (!parentVariantId) {
        this._showError(
          'Unable to add bundle to cart. Missing parent variant. Please refresh and try again.'
        );
        return false;
      }

      if (!this.bundleKey || !this.bundlePrice) {
        this._showError(
          'Unable to add bundle to cart. Missing bundle price or key.'
        );
        return false;
      }

      if (!bundleVariants.length) {
        this._showError(
          'No bundle item selected. Please select a size for each bundle item before adding to cart.'
        );
        return false;
      }

      const missingSelection = bundleVariants.some((item) => !item.variantId);
      if (missingSelection) {
        this._showError(
          'Missing Selection. Please select a size for each bundle item before adding to cart.'
        );
        return false;
      }

      return true;
    }

    /**
     * Build the items payload for /cart/add.js.
     *
     * @param {{ root: HTMLElement, variantId: string|null, metaKey: string|null }[]} bundleVariants
     * @returns {BundleLinePayload[]}
     * @private
     */
    _buildItemsPayload(bundleVariants) {
      /** @type {BundleLinePayload[]} */
      const items = [];
      const bundleId = this.bundleKey;
      const msrpDiscountFactor = this._getMsrpDiscountFactor(bundleVariants);

      bundleVariants.forEach((item, _) => {
        if (!item.variantId) return;

        const msrpBasedPrice = this._getMsrpBasedPrice(item);

        const itemMetainfo = this._getMetaInfoForKey(item.metaKey);
        const childProps = this._buildBaseProperties(
          itemMetainfo,
          bundleId,
          msrpBasedPrice,
          msrpDiscountFactor
        );

        items.push({
          id: item.variantId,
          quantity: 1,
          properties: childProps,
        });
      });

      return items;
    }

    /**
     * Validate that all items have required properties.
     *
     * @param {BundleLinePayload[]} items
     * @returns {boolean}
     * @private
     */
    _validateItemsProperties(items) {
      const requiredProps = TeamStoreBundleCart.REQUIRED_PROPERTIES;

      const missingPropsOnAny = items.some((line) => {
        const props = line.properties || {};
        return requiredProps.some(
          (key) =>
            !Object.prototype.hasOwnProperty.call(props, key) ||
            props[key] === ''
        );
      });

      if (missingPropsOnAny) {
        this._showError(
          'Unable to add bundle to cart. Some bundle configuration is missing. Please refresh and try again.'
        );
        return false;
      }

      return true;
    }

    /**
     * @param {BundleLinePayload[]} items
     * @returns {Promise<void>}
     * @private
     */
    async _submitItems(items) {
      this._setSubmitting(true);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items,
          sections: this.CartDrawer.getSectionsToRender().map(
            /** @param {{ id: string }} section */
            (section) => section.id
          ),
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          this.CartDrawer.renderContents(data);
        })
        .catch((error) => {
          console.error('[TeamStoreBundleCart] Unexpected error:', error);
          this._showError(
            'Something went wrong while adding the bundle to cart. Please try again.'
          );
        })
        .finally(() => {
          this._setSubmitting(false);
          // Move handling in cart-drawer.js and remove here and in product-form.js
          if (this.CartDrawer && this.CartDrawer.classList.contains('is-empty'))
            this.CartDrawer.classList.remove('is-empty');
        });
    }

    /**
     * Collect selected variant ids + metaobject key for all <bundle-variant-radios>.
     *
     * @returns {{ root: HTMLElement, variantId: string|null, metaKey: string|null }[]}
     * @private
     */
    _collectSelectedBundleVariants() {
      const opportunityNumber = this._getOpportunityNumber();

      return this.bundleRadiosRoots.map((root) => {
        const variantId = root.getAttribute('data-selected-variant-id');
        const parentSku = root.dataset.parentSku || null;

        return {
          root,
          variantId: variantId ? String(variantId) : null,
          metaKey: this._buildMetaobjectHandle(parentSku, opportunityNumber),
        };
      });
    }

    /**
     * Build cart properties for a single bundle line.
     *
     * @param {TeamStoreMetaEntry|null} entry
     * @param {string | null} bundleId
     * @param {number} msrpBasedPrice
     * @param {number} msrpDiscountFactor
     * @returns {Record<string, any>}
     * @private
     */
    _buildBaseProperties(
      entry,
      bundleId,
      msrpBasedPrice,
      msrpDiscountFactor
    ) {
      const meta = entry?.metaobject || {};
      const team_product_data =
        entry?.teamStoreCollection?.team_product_data || null;
      const discountPercent = this._getBundleDiscountPercent();

      /** @type {Record<string, any>} */
      const props = {};

      TeamStoreBundleCart.TEAM_STORE_METAOBJECT_PROPERTIES.forEach((key) => {
        if (key in meta) {
          props[`_ts_${key}`] = meta[key];
        }
      });

      props['_ts_bundle_id'] = bundleId;
      props['_ts_bundle_price'] = String(this.bundlePrice);
      props['_ts_bundle_discount'] = String(discountPercent);
      props['Custom Gear Order'] = 'TRUE';

      // Only for team store items
      if (meta && Object.keys(meta).length > 0) {
        props['_ts_price'] = String(this._getLinePrice(meta));
        props['_ts_price_level'] = this.teamProductData.priceLevel;
        props['_ts_image'] = this._isJSON(team_product_data)
          ? JSON.parse(team_product_data)?.[meta.entry_sku]?.imageLink
          : undefined;
      }

      props['_bundle_name'] =
        this.viewModel.$bundleName?.textContent?.trim() || '';

      props['_line_final_price'] = String(
        (msrpBasedPrice * msrpDiscountFactor).toFixed(2)
      );

      props['_apz_scene_id'] ??= '';
      props['_apz_selling_plan'] ??= 'one_time';
      props['_apz_event_id'] ??= 'one_time';

      return props;
    }

    /**
     * @param {string} message
     * @private
     */
    _showError(message) {
      const { $errorWrapper, $errorMessage } = this.viewModel;

      if ($errorWrapper && $errorMessage) {
        $errorMessage.textContent = message;
        $errorWrapper.hidden = false;
        $errorWrapper.setAttribute('aria-hidden', 'false');
        return;
      }
    }

    /**
     * @private
     */
    _clearError() {
      const { $errorWrapper, $errorMessage } = this.viewModel;

      if ($errorWrapper && $errorMessage) {
        $errorMessage.textContent = '';
        $errorWrapper.hidden = true;
        $errorWrapper.setAttribute('aria-hidden', 'true');
      }
    }

    /**
     * Toggle submitting state on button.
     * @param {boolean} isSubmitting
     * @private
     */
    _setSubmitting(isSubmitting) {
      this._isSubmitting = isSubmitting;

      const { $addToCartButton } = this.viewModel;
      if (!$addToCartButton) return;

      if (isSubmitting) {
        $addToCartButton.disabled = true;
        $addToCartButton.setAttribute('aria-disabled', 'true');
        return;
      }

      this._syncAddToCartState();
    }

    /**
     * Disable Add to Cart if ANY Team Store item is marked closed.
     * @private
     */
    _checkStoreClosedState() {
      const objects = window.TeamStoreMetaobjects || {};

      const anyClosed = Object.values(objects).some((entry) => {
        const meta = entry?.metaobject || entry;
        return meta?.isTeamStoreClosed === true;
      });

      if (!anyClosed) return;

      const { $addToCartButton } = this.viewModel;

      if ($addToCartButton) {
        $addToCartButton.disabled = true;
        $addToCartButton.setAttribute('aria-disabled', 'true');
        $addToCartButton.setAttribute('store-closed', '');
        $addToCartButton.textContent =
          window.variantStrings.storeUnavailable || 'Store Unavailable';
      }
    }

    /**
     * @param {{ root: HTMLElement, metaKey: string|null }} item
     * @returns {number} Price in dollars
     * @private
     */
    _getMsrpBasedPrice(item) {
      const entry = this._getMetaInfoForKey(item.metaKey);
      const meta = entry?.metaobject || {};

      if (Object.keys(meta).length > 0) {
        return this._getLinePrice(meta);
      }

      const msrp = Number(item.root?.dataset?.msrp || 0);
      const sellingPrice = Number(item.root?.dataset?.productPrice || 0);
      const raw = msrp > 0 ? msrp : sellingPrice;

      return Number.isFinite(raw) ? raw / 100 : 0;
    }

    /**
     * discount_rate = bundle_price / sum (MSRP prices)
     * @param {{ root: HTMLElement, variantId: string|null, metaKey: string|null }[]} bundleVariants
     * @returns {number}
     * @private
     */
    _getMsrpDiscountFactor(bundleVariants) {
      let bundlePrice = Number(this.bundlePrice);
      if (bundlePrice > 999) bundlePrice /= 100;

      const msrpPrices = bundleVariants.map((item) =>
        this._getMsrpBasedPrice(item)
      );

      const sum = msrpPrices.reduce((a, b) => a + b, 0);
      if (!sum || !bundlePrice) return 1;

      return bundlePrice / sum;
    }

    /**
     * Resolve metaobject & collection info for a metaKey.
     *
     * @param {string|null} key
     * @returns {TeamStoreMetaEntry|null}
     * @private
     */
    _getMetaInfoForKey(key) {
      if (!key) return null;

      const entry = window.TeamStoreMetaobjects?.[key];
      if (!entry) return null;

      return entry;
    }

    /**
     * Compute correct line price using TeamStorePriceCalculator
     * @param {Record<string, any>} metaobject
     * @returns {number}
     * @private
     */
    _getLinePrice(metaobject) {
      const calc = new window.teamStoreUtilities.PriceCalculator(metaobject, {
        priceLevel: this.teamProductData.priceLevel,
        isTeamAdmin: this.teamProductData.isTeamAdmin,
      });

      return calc.getPrice(1); // Always qty=1 for bundle items
    }

    /**
     * Calculate percentage discount relative to sum of child products.
     * Result: (sum - bundlePrice) / sum
     * @returns {number} discount percent (0-1), max 5 decimals
     * @private
     */
    _getBundleDiscountPercent() {
      const childrenPrices = this.bundleRadiosRoots
        .map((root) => Number(root.dataset.productPrice || 0))
        .filter((n) => !Number.isNaN(n) && n > 0);
      const sum = childrenPrices.reduce((a, b) => a + b, 0);
      const bundlePrice = Number(this.bundlePrice);

      if (!sum || !bundlePrice) return 0;

      const fractionOff = (sum - bundlePrice) / sum;
      const percentOff = fractionOff * 100;

      return Number(percentOff.toFixed(5));
    }

    /**
     * Determine parent variant id.
     * @returns {string|null|undefined}
     * @private
     */
    get bundleVariantId() {
      return this.teamProductData.bundleId;
    }

    /**
     * Unique bundle key: `${parentVariantId}_${timestamp}`
     * @private
     */
    get bundleKey() {
      const parentVariantId = this.bundleVariantId;
      if (!parentVariantId) return null;

      return `${parentVariantId}_${Date.now()}`;
    }

    /**
     * Bundle price (from teamProductData)
     * @private
     */
    get bundlePrice() {
      return this.teamProductData?.bundlePrice ?? null;
    }

    get teamProductData() {
      return window.teamProductData || {};
    }

    /**
     * Safely check if value is valid JSON string
     * @param {string|undefined|null} val
     * @returns {boolean}
     * @private
     */
    _isJSON(val) {
      if (typeof val !== 'string') return false;
      try {
        JSON.parse(val);
        return true;
      } catch {
        return false;
      }
    }
  }

  new TeamStoreBundleCart();
} catch (e) {
  console.error('[TeamStoreBundleCart] Initialization error:', e);
}
